import React, { ChangeEvent, useContext, useState } from 'react'
import { FormField } from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  PaymentAgentProfileDto,
  UpdatePaymentAgentProfileDto,
} from 'api/Wallet/Types/PaymentAgentDto'

import { NameDto } from '../../../../api/Common/Types'
import { errorToast, ToastContext } from '../../../../components/Toast/Toast'

type SocialMediaType = 'facebook' | 'whatsapp' | 'telegram'

const SOCIAL_MEDIA_PREFIXES: Record<SocialMediaType, string> = {
  facebook: 'https://m.me/',
  whatsapp: 'https://wa.me/',
  telegram: 'https://t.me/',
}

interface Props {
  data: PaymentAgentProfileDto
  socialMediaTypes: NameDto[]
  handleSaveProfile: (data: UpdatePaymentAgentProfileDto) => void
  onClose: () => void
  isOpen?: boolean
}

const prepareData = (socialMediaTypes: NameDto[], data: PaymentAgentProfileDto) => {
  const socialMediaMap = data.socialMedia.reduce(
    (acc, socialMedia) => {
      acc[socialMedia.socialMediaType.name] = { value: socialMedia.value, enabled: true, error: '' }
      return acc
    },
    {} as Record<string, { value: string; enabled: boolean; error: string }>
  )

  socialMediaTypes.forEach((socialMediaType) => {
    if (!socialMediaMap[socialMediaType.name]) {
      socialMediaMap[socialMediaType.name] = { value: '', enabled: false, error: '' }
    }
  })

  return socialMediaMap
}

const validateUrl = (type: SocialMediaType, url: string): string => {
  if (!url) return 'URL cannot be empty'

  const prefix = SOCIAL_MEDIA_PREFIXES[type.toLowerCase() as SocialMediaType]
  if (!prefix) return ''

  if (!url.toLowerCase().startsWith(prefix.toLowerCase())) {
    return `URL must start with ${prefix}`
  }
  return ''
}

export const PaymentAgentSocialMediaProfileModal: React.FC<Props> = (props) => {
  const { data, handleSaveProfile, socialMediaTypes, isOpen, onClose } = props
  const [profiles, setProfiles] = useState(prepareData(socialMediaTypes, data))
  const setToast = useContext(ToastContext)

  const validateProfiles = () => {
    let hasErrors = false
    const newProfiles = { ...profiles }

    socialMediaTypes.forEach((socialMediaType) => {
      if (profiles[socialMediaType.name].enabled) {
        const error = validateUrl(
          socialMediaType.name as SocialMediaType,
          profiles[socialMediaType.name].value
        )
        newProfiles[socialMediaType.name].error = error
        if (error) {
          hasErrors = true
        }
      }
    })

    setProfiles(newProfiles)
    return !hasErrors
  }

  const handleChange = (name: SocialMediaType, value: string) => {
    const error = validateUrl(name, value)
    setProfiles({
      ...profiles,
      [name]: { ...profiles[name], value, error },
    })
  }

  const handleSubmitForm = () => {
    if (!validateProfiles()) {
      return setToast(errorToast('Please correct the errors in the form'))
    }

    const enabledProfilesToSave = socialMediaTypes
      .filter((socialMediaType) => profiles[socialMediaType.name].enabled)
      .map((socialMediaType) => ({
        typeId: socialMediaType.id,
        value: profiles[socialMediaType.name].value,
      }))

    handleSaveProfile({ ...data, socialMedia: enabledProfilesToSave })
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Agent Profile</DialogTitle>
        </DialogHeader>
        {socialMediaTypes.map((socialMediaType) => (
          <FormField
            label={
              <div className='flex items-center gap-2'>
                {socialMediaType.name}
                <Checkbox
                  checked={profiles[socialMediaType.name].enabled}
                  onCheckedChange={(checked) =>
                    setProfiles({
                      ...profiles,
                      [socialMediaType.name]: {
                        ...profiles[socialMediaType.name],
                        enabled: checked,
                        error: '',
                      },
                    })
                  }
                />
              </div>
            }
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              handleChange(socialMediaType.name as SocialMediaType, e.target.value)
            }
            error={
              profiles[socialMediaType.name].enabled ? profiles[socialMediaType.name].error : ''
            }
            value={profiles[socialMediaType.name].value}
          />
        ))}
        <DialogFooter>
          <Button onClick={handleSubmitForm}>{'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
